import React, {
  Children,
  ReactElement,
  ReactNode,
  cloneElement,
  useState,
} from "react";
import { toPng } from "html-to-image";
import { notifyError } from "@linkfluence/core/notifications";
import { Locale } from "@linkfluence/models/locale";
import Tile from "@radarly/components/cards/card";
import { tr } from "@radarly/i18n";
import { DatavizName } from "@radarly/models/dataviz";

const TILE_HEADER_HEIGHT = 108;

const isPaginatedCard = (children: ReactElement) => {
  const dataviz = children?.props?.card?.dataviz?.name;
  return (
    dataviz === DatavizName.HorizontalBarChart ||
    dataviz === DatavizName.HorizontalStackedBarChart
  );
};

export const useGeneratePNG = (
  nodeID: string,
  title: string,
  height: number,
  locale: Locale
) => {
  const [cardPNG, setCardPNG] = useState<ReactElement>(null);

  const createCard = (children: ReactElement, subtitle: ReactNode) => (
    <div
      id={`${nodeID}_clone`}
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: 8,
        background: "inherit",
        zIndex: -99,
      }}
    >
      <Tile title={title} height={height} subtitle={subtitle} actions={null}>
        {Children.map(children, (child) =>
          cloneElement(child, { isPNG: true })
        )}
      </Tile>
    </div>
  );

  const onGeneratePNG = async (children: ReactElement, subtitle: ReactNode) => {
    try {
      let id = `#${nodeID}`;
      if (isPaginatedCard(children)) {
        setCardPNG(createCard(children, subtitle));
        id = `#${nodeID}_clone`;
        await new Promise((res) => setTimeout(() => res(true), 2000));
      }
      const cardNode = document.querySelector(id);
      if (cardNode) {
        const data = await toPng(cardNode as HTMLElement, {
          width: undefined,
          height: height + TILE_HEADER_HEIGHT,
          cacheBust: true,
          quality: 1, // best quality
        });
        const link = document.createElement("a");
        link.href = data;
        link.download = `${title}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setCardPNG(null);
      }
    } catch (err) {
      return notifyError(tr(locale, "INSIGHT_PAGES_PNG_EXPORT_ERROR"));
    }
  };

  return {
    onGeneratePNG,
    cardPNG,
  };
};
